/**
 * Adding a node by hand: the "Add node" window's service. A right click on the graph opens the
 * window beside the pointer and the node goes where the click was; the command `node.add`
 * (key N) opens it with no place, and the node then goes beside the selected node, or where the
 * layout puts it. A link drawn from a node and dropped on empty space opens it too: the new
 * node goes there, linked from that node (the kind the two kinds suggest), in one undo; a
 * blueprint pin's wire dropped there links it from that pin (the kind the environment plays). A
 * page's wiki link to a name no node has opens it with that name typed in. The
 * window itself only shows the fields; this sends the node to the core, shows its categories,
 * selects it and closes the window.
 */
import { createSignal, type Accessor } from "solid-js";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Actions, NewNode } from "../client/actions";
import type { CommandSet } from "../shared/commands";
import type { MenuSection, ToolbarButton } from "../shared/menus";
import { toast } from "../shared/toast";
import type { Spot } from "../shared/spot";
import type { WorldPoint } from "./presence";
import type { Scene } from "./scene";
import type { View } from "./view";

/** A point on the page, in CSS pixels from its top left (like a mouse event's `clientX`/`clientY`). */
export interface PagePoint {
  x: number;
  y: number;
}

/** What the service needs. */
export interface NodeAdderDeps {
  actions: Actions;
  view: View;
  scene: Scene;
  /** Shows the window, beside `near` when given (the shell floats it there). */
  show: (near: PagePoint | null) => void;
  /** Hides the window. */
  hide: () => void;
  /** The `define_kinds` edit a plugin's node kind needs first (none: the graph has it). */
  toDefine?: (nodes: string[], links: string[]) => GraphEdit[];
  /** The kind of link offered from node `from` to a new node of kind `kind` (`null`: none fits); `wired`: from one of its pins (a blueprint wire). */
  linkKind?: (from: string, kind: string, wired: boolean) => string | null;
}

/** The "Add node" service. */
export interface NodeAdder {
  /** Where the next node goes in the graph (`null`: beside the selection, or where the layout puts it). */
  at: Accessor<WorldPoint | null>;
  /** How many times the window was asked for: the window focuses its name field again on each. */
  asked: Accessor<number>;
  /** The node the new one will be linked from (a link dropped on empty space), if any. */
  from: Accessor<string | null>;
  /** The name typed in when the window opens (a wiki link's name; empty: none). */
  name: Accessor<string>;
  /** The pin of `from` the new node is wired from (a blueprint wire dropped on empty space), if any. */
  pin: Accessor<string | null>;
  /** The project's assets the new node will be attached to (a file dropped on the graph). */
  attachments: Accessor<string[]>;
  /** Attaches the next node to these assets (call right after {@link NodeAdder.open}). */
  attach(paths: string[]): void;
  /**
   * Puts the next node at `spot` of the view shown (call right after {@link NodeAdder.open}): its
   * diagram box or board rectangle there; the nodal view's point is `open`'s `at`.
   */
  placeAt(spot: Spot | null): void;
  /**
   * Opens the window for a node at `at` (a graph point), shown beside `near` (a page point),
   * linked from node `from` when given (from its pin `pin` when given), `name` typed in.
   */
  open(at?: WorldPoint | null, near?: PagePoint | null, from?: string | null, name?: string, pin?: string | null): void;
  /** Closes the window without adding anything. */
  close(): void;
  /** Adds the node, selects it and closes the window; the new node's id, or `null` when the core refused it. */
  add(node: NewNode): Promise<string | null>;
}

/** Creates the service. */
export function createNodeAdder({ actions, view, scene, show, hide, toDefine, linkKind }: NodeAdderDeps): NodeAdder {
  const [at, setAt] = createSignal<WorldPoint | null>(null);
  const [from, setFrom] = createSignal<string | null>(null);
  const [name, setName] = createSignal("");
  const [pin, setPin] = createSignal<string | null>(null);
  const [attachments, setAttachments] = createSignal<string[]>([]);
  const [asked, setAsked] = createSignal(0);
  /** Where the next node goes in a view other than the nodal one (`null`: where the view puts it). */
  let spot: Spot | null = null;

  /**
   * Where the node is put: the chosen point, or beside the selected node. `null`: the layout
   * finds it a place (a node put somewhere stays there until the layout settles around it, so
   * nodes put in the same spot would overlap).
   */
  const place = (): WorldPoint | null => {
    const chosen = at();
    if (chosen) return chosen;
    const s = view.state.selection;
    const near = s?.type === "node" ? scene.byId.get(s.id) : undefined;
    return near?.visible ? { x: near.x + (Math.random() - 0.5) * 70, y: near.y + (Math.random() - 0.5) * 70, z: near.z } : null;
  };

  return {
    at,
    asked,
    from,
    name,
    pin,
    attachments,
    attach: setAttachments,
    placeAt(at) {
      spot = at;
    },
    open(point = null, near = null, source = null, typed = "", wiredFrom = null) {
      setAttachments([]);
      spot = null;
      setAt(point);
      setFrom(source);
      setPin(source ? wiredFrom : null);
      setName(typed);
      setAsked((n) => n + 1);
      show(near);
    },
    close() {
      setAttachments([]);
      spot = null;
      setAt(null);
      setFrom(null);
      setPin(null);
      hide();
    },
    async add(node) {
      const name = node.name.trim();
      if (!name) {
        toast("Enter a name first.");
        return null;
      }
      // Shown in the graph, so the new node does not vanish behind a filter.
      for (const c of node.categories.length ? node.categories : [""]) view.showCategory(c);
      const p = place();
      if (p) scene.hint(name, p.x, p.y, p.z);
      const source = from();
      const wired = pin();
      const kind = source ? (linkKind?.(source, node.kind, wired !== null) ?? null) : null;
      const links = source && kind ? [{ kind, to: source, label: "", incoming: true, ...(wired ? { pin: wired } : {}) }] : [];
      const attached = attachments();
      const placed = spot?.place(node.kind) ?? {};
      const first = [...(toDefine ? toDefine([node.kind], kind ? [kind] : []) : []), ...(spot?.first ?? [])];
      const added = await actions.addNode({ ...node, name, ...placed, ...(attached.length ? { attachments: attached } : {}) }, links, first);
      if (!added) return null;
      for (const why of added.warnings) toast(why, "error");
      toast(source ? `Added ${name}, linked.` : `Added ${name}. Drag from its handle to link it.`);
      view.selectNode(added.id);
      setAttachments([]);
      spot = null;
      setAt(null);
      setFrom(null);
      setPin(null);
      hide();
      return added.id;
    },
  };
}

/** The command `node.add` (key N): the "Add node" window, for a node beside the selection. */
export function nodeAdderCommands(adder: NodeAdder): CommandSet {
  return { "node.add": () => adder.open() };
}

/** The Edit menu's "Add a node…". */
export function nodeAdderMenus(): MenuSection[] {
  return [{ menu: "edit", section: "Graph", order: 20, entries: () => [{ label: "Add a node…", command: "node.add" }] }];
}

/** The toolbar's Add node. */
export function nodeAdderToolbar(): ToolbarButton[] {
  return [{ id: "tool-add-node", section: "create", order: 30, icon: "add", label: "Add node", title: "Add a node beside the selection", command: "node.add" }];
}
